import type { BrowserAudioPlaybackPort, BrowserAudioPlaybackSession } from '../../../ports/BrowserAudioPlaybackPort';
import type { TextToSpeechResult } from '../../../ports/TextToSpeechPort';

const PLAYBACK_START_TIMEOUT_MS = 2_000;

export class BrowserAudioPlayback implements BrowserAudioPlaybackPort {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private audioUnlockInstalled = false;

  constructor() {
    this.installAudioUnlock();
  }

  async play(result: TextToSpeechResult): Promise<BrowserAudioPlaybackSession> {
    if (!isBrowserPlayableAudio(result.contentType)) {
      throw new Error(`Browser audio playback does not support ${result.contentType || 'unknown audio type'}.`);
    }

    const webAudioResult = await this.tryPlayWithWebAudio(result.audio);
    if (webAudioResult.session) return webAudioResult.session;

    return playWithHtmlAudio(result, webAudioResult.stage);
  }

  private async tryPlayWithWebAudio(audio: Blob): Promise<WebAudioPlaybackResult> {
    const context = this.ensureAudioContext();
    if (!context || !this.gainNode) return { session: null, stage: 'browser_audio_webaudio_unavailable' };

    try {
      if (context.state === 'suspended') {
        try {
          await withPlaybackStartupTimeout(context.resume());
        } catch {
          return { session: null, stage: 'browser_audio_webaudio_resume_failed' };
        }
      }

      let encodedAudio: ArrayBuffer;
      try {
        encodedAudio = await withPlaybackStartupTimeout(audio.arrayBuffer());
      } catch {
        return { session: null, stage: 'browser_audio_webaudio_blob_read_failed' };
      }

      let audioBuffer: AudioBuffer;
      try {
        audioBuffer = await withPlaybackStartupTimeout(context.decodeAudioData(encodedAudio.slice(0)));
      } catch {
        return { session: null, stage: 'browser_audio_webaudio_decode_failed' };
      }
      const source = context.createBufferSource();
      let stopped = false;
      let endedCallback: (() => void) | null = null;
      source.buffer = audioBuffer;
      source.connect(this.gainNode);
      source.onended = () => {
        if (stopped) return;
        stopped = true;
        try {
          source.disconnect();
        } catch {
          // Source may already be disconnected.
        }
        endedCallback?.();
      };
      try {
        source.start();
      } catch {
        try {
          source.disconnect();
        } catch {
          // Source may already be disconnected.
        }
        return { session: null, stage: 'browser_audio_webaudio_source_start_failed' };
      }

      return { session: {
        durationMs: Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0
          ? audioBuffer.duration * 1000
          : undefined,
        stop: () => {
          if (stopped) return;
          stopped = true;
          try {
            source.stop();
          } catch {
            // Source may already have ended; stop remains idempotent.
          }
          try {
            source.disconnect();
          } catch {
            // Source may already be disconnected.
          }
        },
        onEnded: (callback) => {
          endedCallback = callback;
        },
      }, stage: 'browser_audio_webaudio_started' };
    } catch {
      return { session: null, stage: 'browser_audio_webaudio_failed' };
    }
  }

  private ensureAudioContext(): AudioContext | null {
    const AudioContextConstructor = globalThis.AudioContext ?? (globalThis as AudioContextGlobal).webkitAudioContext;
    if (!AudioContextConstructor) return null;

    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContextConstructor();
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 1;
      this.gainNode.connect(this.audioContext.destination);
    }

    this.installAudioUnlock();
    return this.audioContext;
  }

  private installAudioUnlock(): void {
    if (this.audioUnlockInstalled || typeof document === 'undefined') return;
    this.audioUnlockInstalled = true;

    const removeUnlockListeners = () => {
      document.removeEventListener('pointerdown', unlock, { capture: true });
      document.removeEventListener('touchstart', unlock, { capture: true });
      document.removeEventListener('keydown', unlock, { capture: true });
      document.removeEventListener('touchend', unlock, { capture: true });
      document.removeEventListener('click', unlock, { capture: true });
    };

    const unlock = async () => {
      const context = this.ensureAudioContext();
      if (!context) {
        removeUnlockListeners();
        return;
      }

      if (context.state === 'suspended') {
        try {
          await context.resume();
        } catch {
          // Keep the gesture listeners installed so a later participant tap/press can retry unlock.
          return;
        }
      }

      if (context.state === 'running') {
        removeUnlockListeners();
      }
    };

    const options = { capture: true, passive: true };
    document.addEventListener('pointerdown', unlock, options);
    document.addEventListener('touchstart', unlock, options);
    document.addEventListener('keydown', unlock, options);
    // Phones only grant permission to start sound when a tap ends, not when it starts.
    document.addEventListener('touchend', unlock, options);
    document.addEventListener('click', unlock, options);
  }
}

interface WebAudioPlaybackResult {
  session: BrowserAudioPlaybackSession | null;
  stage: BrowserAudioStage;
}

type BrowserAudioStage =
  | 'browser_audio_webaudio_unavailable'
  | 'browser_audio_webaudio_resume_failed'
  | 'browser_audio_webaudio_blob_read_failed'
  | 'browser_audio_webaudio_decode_failed'
  | 'browser_audio_webaudio_source_start_failed'
  | 'browser_audio_webaudio_started'
  | 'browser_audio_webaudio_failed';

type AudioContextGlobal = typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

async function playWithHtmlAudio(result: TextToSpeechResult, webAudioStage: BrowserAudioStage): Promise<BrowserAudioPlaybackSession> {
  if (typeof Audio === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('Browser audio playback is not supported here.');
  }

  const objectUrl = URL.createObjectURL(result.audio);
  const audio = new Audio();
  let stopped = false;
  let endedCallback: (() => void) | null = null;
  audio.src = objectUrl;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    audio.pause();
    URL.revokeObjectURL(objectUrl);
  };

  audio.addEventListener('ended', () => {
    if (stopped) return;
    stopped = true;
    URL.revokeObjectURL(objectUrl);
    endedCallback?.();
  }, { once: true });

  try {
    await withPlaybackStartupTimeout(audio.play());
  } catch {
    stop();
    const error = new Error('browser_audio_html_play_failed') as Error & { webAudioStage?: BrowserAudioStage };
    error.webAudioStage = webAudioStage;
    throw error;
  }

  return {
    durationMs: Number.isFinite(audio.duration) && audio.duration > 0
      ? audio.duration * 1000
      : undefined,
    stop,
    onEnded: (callback) => {
      endedCallback = callback;
    },
  };
}

function isBrowserPlayableAudio(contentType: string): boolean {
  const normalized = contentType.toLowerCase().split(';', 1)[0].trim();
  return normalized === 'audio/mpeg'
    || normalized === 'audio/mp3'
    || normalized === 'audio/wav'
    || normalized === 'audio/x-wav'
    || normalized === 'audio/webm'
    || normalized === 'audio/ogg'
    || normalized === 'audio/mp4'
    || normalized === 'audio/aac';
}

async function withPlaybackStartupTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Browser audio playback startup timed out.'));
        }, PLAYBACK_START_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
